import React, { useRef, useEffect } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { cn } from '@/utils/cn';

interface LiquidBarProps {
  children: React.ReactNode;
  className?: string;
  position?: 'top' | 'bottom' | 'center';
  variant?: 'minimal' | 'vibrant' | 'frosted';
}

export const LiquidBar: React.FC<LiquidBarProps> = ({
  children,
  className,
  position = 'top',
  variant = 'minimal',
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const springConfig = { damping: 30, stiffness: 200 };
  const x = useSpring(mouseX, springConfig);
  const y = useSpring(mouseY, springConfig);

  const rotateX = useTransform(y, [-10, 10], [1.0, -1.0]);
  const rotateY = useTransform(x, [-10, 10], [-1.0, 1.0]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      mouseX.set((e.clientX - centerX) * 0.1);
      mouseY.set((e.clientY - centerY) * 0.1);
    };

    const handleMouseLeave = () => {
      mouseX.set(0);
      mouseY.set(0);
    };

    const element = ref.current;
    if (element) {
      element.addEventListener('mousemove', handleMouseMove);
      element.addEventListener('mouseleave', handleMouseLeave);
      return () => {
        element.removeEventListener('mousemove', handleMouseMove);
        element.removeEventListener('mouseleave', handleMouseLeave);
      };
    }
  }, [mouseX, mouseY]);

  const positionClasses = {
    top: 'top-12',
    bottom: 'bottom-6',
    center: '',
  };

  const variants = {
    minimal: {
      bg: 'bg-white/[0.08]',
      border: 'border-white/[0.12]',
      backdrop: 'backdrop-blur-[24px]',
      shadow: 'shadow-xl shadow-black/5',
    },
    vibrant: {
      bg: 'bg-gradient-to-r from-blue-500/[0.1] via-purple-500/[0.08] to-pink-500/[0.1]',
      border: 'border-white/[0.15]',
      backdrop: 'backdrop-blur-[28px]',
      shadow: 'shadow-2xl shadow-blue-500/10',
    },
    frosted: {
      bg: 'bg-white/[0.06]',
      border: 'border-white/[0.1]',
      backdrop: 'backdrop-blur-[20px]',
      shadow: 'shadow-lg shadow-black/5',
    },
  };

  const currentVariant = variants[variant];

  const baseClasses = cn(
    position !== 'center' ? 'fixed left-1/2 transform -translate-x-1/2 z-50' : 'relative',
    'rounded-full border transition-all duration-500 ease-out',
    'px-6 py-3',
    'before:absolute before:inset-0 before:rounded-full',
    'before:bg-gradient-to-r before:from-white/10 before:via-white/5 before:to-white/10',
    'before:opacity-60',
    positionClasses[position],
    currentVariant.bg,
    currentVariant.border,
    currentVariant.backdrop,
    currentVariant.shadow,
    className
  );

  return (
    <motion.div
      ref={ref}
      className={baseClasses}
      style={{ rotateX, rotateY, transformPerspective: 1000 }}
      initial={{ opacity: 0, y: position === 'top' ? -40 : 40, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1],
        opacity: { duration: 0.6 },
        scale: { duration: 0.8, delay: 0.1 },
        y: { duration: 0.8 }
      }}
      whileHover={{
        scale: 1.02,
        y: position === 'top' ? -2 : 2,
        transition: { duration: 0.3 }
      }}
    >
      {/* 액체 배경 애니메이션 */}
      <div className="absolute inset-0 overflow-hidden rounded-full">
        {/* 흐르는 액체 효과 */}
        <motion.div
          className="absolute -left-4 top-1/2 w-8 h-8 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.2) 0%, transparent 70%)',
            filter: 'blur(8px)',
            transform: 'translateY(-50%)',
          }}
          animate={{
            x: [0, 100, 200, 300],
            scale: [1, 1.2, 0.8, 1],
            opacity: [0, 0.6, 0.3, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />

        <motion.div
          className="absolute -right-4 top-1/2 w-6 h-6 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(147, 51, 234, 0.15) 0%, transparent 70%)',
            filter: 'blur(6px)',
            transform: 'translateY(-50%)',
          }}
          animate={{
            x: [0, -80, -160, -240],
            scale: [1, 0.8, 1.2, 1],
            opacity: [0, 0.5, 0.4, 0],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 3,
          }}
        />

        {/* 미묘한 시머 효과 */}
        <motion.div
          className="absolute inset-0 rounded-full opacity-20"
          style={{
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)',
          }}
          animate={{ x: [-100, 400] }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1,
          }}
        />
      </div>

      {/* 유리 반사 */}
      <div className="absolute inset-0 rounded-full overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-1/2 opacity-40"
          style={{
            background: 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)',
          }}
        />
      </div>

      {/* 컨텐츠 */}
      <div className="relative z-10 flex items-center space-x-1">
        {children}
      </div>

      {/* 인터랙티브 글로우 */}
      <motion.div
        className="absolute inset-0 rounded-full pointer-events-none"
        initial={{ opacity: 0 }}
        whileHover={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        style={{
          background: 'radial-gradient(circle at center, rgba(255,255,255,0.05) 0%, transparent 70%)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1)',
        }}
      />
    </motion.div>
  );
};